"use client"

import { useRef, forwardRef, useImperativeHandle } from 'react'
import { WellGeometry, CasingString, FormationTop, ExistingTool } from '../../lib/api/w3-wizard'

// Well header info
interface WellData {
  api14: string
  operator_name: string
  field_name: string
  well_number: string
  lease_name: string
}

// A single as-plugged cement plug
export interface AsPluggedPlug {
  plug_number: number
  type: string
  top_ft: number
  bottom_ft: number
  sacks: number
  cement_class?: string
  tagged_depth_ft?: number
  placement_method?: string
  woc_hours?: number
  woc_tagged?: boolean
  // Fields aligned with ProposedPlugWBD
  formation?: string | null
  plug_type?: string | null        // spot_plug, perf_and_squeeze_plug, etc.
  display_name?: string | null
  details?: {
    annuli?: Array<{
      inner?: string
      outer?: string
      inner_od_in?: number
      outer_id_in?: number
    }>
    geometry_used?: {
      annulus?: string
      casing_id_in?: number
      stinger_od_in?: number
    }
    cement_class?: string
    wait_hours?: number
    merged?: boolean
    merged_steps?: Array<{ formation?: string; top_ft?: number; bottom_ft?: number }>
  }
  recipe?: { class?: string }
}

// Full data needed to render diagram
export interface AsPluggedDiagramData {
  well: WellData
  well_geometry: WellGeometry
  plugs: AsPluggedPlug[]
  jurisdiction: 'TX' | 'NM'
}

interface VisibilitySettings {
  casing: boolean
  tubing: boolean
  perforations: boolean
  formationTops: boolean
  newCementPlugs: boolean
  existingCement: boolean
  bridgePlugs: boolean
  existingTools: boolean
}

interface AsPluggedWBDProps {
  data: AsPluggedDiagramData
  width?: number
  height?: number
  visibility?: VisibilitySettings
}

// Handle for parent to get SVG ref
export interface AsPluggedWBDHandle {
  getSvgElement: () => SVGSVGElement | null
}

// Adapter function: transforms reconciliation comparison rows into AsPluggedDiagramData
export function transformReconciliationToAsPluggedData(
  comparisons: Array<{
    plug_number: number
    planned_type?: string
    actual_type?: string
    actual_top_ft?: number
    actual_bottom_ft?: number
    actual_sacks?: number
    actual_cement_class?: string
    actual_tagged_depth_ft?: number
    actual_placement_method?: string
    actual_woc_hours?: number
    actual_woc_tagged?: boolean
    planned_formation?: string
    actual_formation?: string
    planned_display_name?: string
  }>,
  wellGeometry: WellGeometry | null,
  well: WellData,
  jurisdiction: 'TX' | 'NM' = 'TX',
  _milestoneComparisons?: any[],  // casing cuts, CIBP placements from milestones (reserved for future use)
): AsPluggedDiagramData | null {
  if (!wellGeometry || !comparisons || comparisons.length === 0) return null

  const plugs: AsPluggedPlug[] = comparisons
    .filter(c => c.actual_top_ft != null)
    .map(c => ({
      plug_number: c.plug_number,
      type: c.actual_type || c.planned_type || 'cement_plug',
      top_ft: c.actual_top_ft!,
      bottom_ft: c.actual_bottom_ft ?? c.actual_top_ft!,
      sacks: c.actual_sacks || 0,
      cement_class: c.actual_cement_class,
      tagged_depth_ft: c.actual_tagged_depth_ft,
      placement_method: c.actual_placement_method,
      woc_hours: c.actual_woc_hours,
      woc_tagged: c.actual_woc_tagged,
      formation: c.planned_formation ?? c.actual_formation ?? null,
      plug_type: c.actual_placement_method?.includes('perf') && c.actual_placement_method?.includes('squeeze')
        ? 'perf_and_squeeze_plug'
        : c.actual_placement_method?.includes('perf') && c.actual_placement_method?.includes('circ')
        ? 'perf_and_circulate_plug'
        : 'spot_plug',
      display_name: c.planned_display_name ?? null,
      details: {
        cement_class: c.actual_cement_class,
        wait_hours: c.actual_woc_hours,
      },
      recipe: c.actual_cement_class ? { class: c.actual_cement_class } : undefined,
    }))

  // Also include bridge plug / CIBP entries that may not have top/bottom range
  // but have a single depth
  comparisons.forEach((row: any) => {
    const type = (row.actual_type || row.planned_type || '').toLowerCase()
    if ((type.includes('bridge') || type.includes('cibp')) && !plugs.find(p =>
      Math.abs(p.top_ft - (row.actual_top_ft ?? row.actual_depth_ft ?? 0)) < 5
    )) {
      const depth = row.actual_top_ft ?? row.actual_depth_ft ?? row.planned_top_ft ?? 0
      if (depth > 0) {
        plugs.push({
          plug_number: plugs.length + 1,
          type: 'bridge_plug',
          top_ft: depth,
          bottom_ft: depth + 1,
          sacks: 0,
          formation: row.planned_formation ?? null,
          plug_type: null,
          display_name: 'CIBP',
        })
      }
    }
  })

  // Extract perforations from perf_and_squeeze / perf_and_circulate operations
  // These plugs imply perforations were shot at those depths before cementing
  const perfFromOperations = comparisons
    .filter(c => {
      const method = (c.actual_placement_method || '').toLowerCase()
      return (
        (method.includes('perf') && (method.includes('squeeze') || method.includes('circ'))) &&
        c.actual_top_ft != null &&
        c.actual_bottom_ft != null
      )
    })
    .map(c => {
      // Perforations are shot at the bottom (deeper end) of the plug interval only
      const perfDepth = Math.max(c.actual_top_ft!, c.actual_bottom_ft!)
      return { top_ft: perfDepth, bottom_ft: perfDepth }
    })

  const existingPerfs = wellGeometry?.production_perforations || []
  const mergedPerfs = [...existingPerfs, ...perfFromOperations]

  const mergedGeometry = wellGeometry
    ? { ...wellGeometry, production_perforations: mergedPerfs }
    : wellGeometry

  return { well, well_geometry: mergedGeometry!, plugs, jurisdiction }
}

const formationColors: Record<string, { bg: string; border: string; text: string }> = {
  // TX formations
  WOLFCAMP: { bg: "#FEF3C7", border: "#D97706", text: "#92400E" },
  STRAWN: { bg: "#DBEAFE", border: "#2563EB", text: "#1E40AF" },
  DEVONIAN: { bg: "#FEE2E2", border: "#DC2626", text: "#991B1B" },
  CLEARFORK: { bg: "#D1FAE5", border: "#059669", text: "#065F46" },
  SPRABERRY: { bg: "#E0E7FF", border: "#4F46E5", text: "#3730A3" },
  DEAN: { bg: "#FED7AA", border: "#EA580C", text: "#9A3412" },
  "SAN ANDRES": { bg: "#FEF9C3", border: "#CA8A04", text: "#713F12" },
  "SANTA ROSA": { bg: "#FCE7F3", border: "#DB2777", text: "#9D174D" },
  YATES: { bg: "#E9D5FF", border: "#A855F7", text: "#6B21A8" },
  "SEVEN RIVERS": { bg: "#BAE6FD", border: "#0284C7", text: "#075985" },
  QUEEN: { bg: "#FED7E2", border: "#F43F5E", text: "#9F1239" },
  GRAYBURG: { bg: "#FEE2C5", border: "#F97316", text: "#9A3412" },
  DEFAULT: { bg: "#F3F4F6", border: "#6B7280", text: "#374151" },
}

function getFormationColors(formation: string) {
  const upper = formation.toUpperCase()
  for (const key of Object.keys(formationColors)) {
    if (upper.includes(key)) return formationColors[key]
  }
  return formationColors.DEFAULT
}

const inchesToPixels = (inches: number) => inches * 12.8

const wellCenterX = 280
const labelStartX = wellCenterX + 140
const minLabelGap = 35

interface LabelItem {
  y: number
  text: string
  subtext?: string
  color: string
}

interface AdjustedLabelItem extends LabelItem {
  adjustedY: number
}

const AsPluggedWBD = forwardRef<AsPluggedWBDHandle, AsPluggedWBDProps>(
  function AsPluggedWBD(props, ref) {
    const {
      data,
      width = 900,
      height: propHeight,
      visibility = {
        casing: true, tubing: true, perforations: true, formationTops: true,
        newCementPlugs: true, existingCement: true, bridgePlugs: true, existingTools: true,
      },
    } = props
    const svgRef = useRef<SVGSVGElement>(null)

    useImperativeHandle(ref, () => ({
      getSvgElement: () => svgRef.current,
    }))

    const svgWidth = width

    // Early guard: ensure well_geometry and all its array properties are safe
    const wellGeometry = data.well_geometry ?? {}
    const casingStrings = wellGeometry.casing_strings || []
    const formationTops = wellGeometry.formation_tops || []
    const productionPerforations = wellGeometry.production_perforations || []
    const tubing = wellGeometry.tubing || []
    const existingTools = wellGeometry.existing_tools || []
    const mechanicalEquipment = wellGeometry.mechanical_equipment || []
    const liner = wellGeometry.liner || []

    // Guard: collect all depths safely
    const depths = [
      ...casingStrings.map((c: CasingString) => c.bottom_ft),
      ...data.plugs.map(p => p.bottom_ft),
      ...data.plugs.map(p => p.top_ft),
      ...productionPerforations.map((p: { bottom_ft: number }) => p.bottom_ft),
      ...tubing.map((t: { bottom_ft: number }) => t.bottom_ft),
      ...formationTops.map((f: FormationTop) => f.top_ft),
      ...existingTools.map((t: ExistingTool) => t.depth_ft || t.top_ft || t.bottom_ft).filter(Boolean),
      ...mechanicalEquipment.map((e: any) => e.depth_ft).filter(Boolean),
      ...liner.map((l: any) => l.bottom_ft).filter(Boolean),
      ...(wellGeometry.kop_ft != null ? [wellGeometry.kop_ft] : []),
    ].filter(d => d != null && isFinite(d))

    const maxDepth = depths.length > 0 ? Math.max(...depths) : 1000

    // Dynamic SVG height scaling
    const pxPerFt = maxDepth <= 2000 ? 0.45
      : maxDepth <= 5000 ? 0.25
      : maxDepth <= 10000 ? 0.12
      : 0.08

    const svgHeight = Math.max(600, Math.ceil(maxDepth * pxPerFt) + 150)
    const depthScale = (svgHeight - 100) / maxDepth
    const depthToY = (depth: number) => 50 + depth * depthScale

    // Adaptive tick intervals
    const tickInterval = maxDepth < 2000 ? 100
      : maxDepth < 5000 ? 250
      : maxDepth < 10000 ? 500
      : 1000

    // Sort casing strings by hole size (largest to smallest)
    const sortedCasing = [...casingStrings].sort(
      (a, b) => (b.hole_size_in ?? b.size_in ?? 0) - (a.hole_size_in ?? a.size_in ?? 0)
    )

    // Build labels for each plug
    const rawLabels: LabelItem[] = data.plugs.map(plug => {
      const midDepth = (plug.top_ft + plug.bottom_ft) / 2

      // Line 1: formation name if available, then placement method, then plug type
      const methodLabel = plug.placement_method
        ? plug.placement_method.replace(/_/g, ' ').replace(/\band\b/g, '&')
        : plug.type.replace(/_/g, ' ')
      const labelText = plug.formation
        ? `${plug.formation} Plug`
        : plug.display_name
        ? plug.display_name
        : `Plug ${plug.plug_number}: ${methodLabel}`

      // Line 2: depth range + sacks + class
      const cementClass = plug.cement_class || plug.recipe?.class || plug.details?.cement_class || ''
      const subtextLine1 = `${plug.top_ft.toLocaleString()}-${plug.bottom_ft.toLocaleString()} ft ${plug.sacks} sks${cementClass ? ` Class ${cementClass}` : ''}`

      // Line 3: WOC + Tag (show for any plug with WOC data)
      const wocHours = plug.woc_hours || plug.details?.wait_hours
      const subtextLine2 = wocHours
        ? `WOC ${wocHours}hrs & Tag TOC`
        : plug.woc_tagged
        ? 'WOC & Tag TOC'
        : ''

      const subtextParts = [subtextLine1]
      if (subtextLine2) subtextParts.push(subtextLine2)

      return {
        y: depthToY(midDepth),
        text: labelText,
        subtext: subtextParts.join('\n'),
        color: '#16a34a',
      }
    })

    // Sort and apply anti-collision
    rawLabels.sort((a, b) => a.y - b.y)
    const adjustedLabels: AdjustedLabelItem[] = []
    rawLabels.forEach((label, idx) => {
      if (idx === 0) {
        adjustedLabels.push({ ...label, adjustedY: label.y })
        return
      }
      const prevY = adjustedLabels[idx - 1].adjustedY
      const newY = label.y - prevY < minLabelGap ? prevY + minLabelGap : label.y
      adjustedLabels.push({ ...label, adjustedY: newY })
    })

    const totalSacks = data.plugs.reduce((sum, p) => sum + p.sacks, 0)

    return (
      <div className="w-full h-full bg-gray-50 p-8 overflow-auto">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6">
          {/* Header */}
          <div className="mb-6 pb-4 border-b-2 border-brand-700">
            <h1 className="text-2xl font-bold text-brand-700 mb-2">As Plugged Wellbore Diagram</h1>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Well:</span>{" "}
                <span className="font-semibold">
                  {data.well.lease_name} {data.well.well_number}
                </span>
              </div>
              <div>
                <span className="text-gray-600">API:</span>{" "}
                <span className="font-semibold">{data.well.api14}</span>
              </div>
              <div>
                <span className="text-gray-600">Operator:</span>{" "}
                <span className="font-semibold">{data.well.operator_name}</span>
              </div>
              <div>
                <span className="text-gray-600">Field:</span>{" "}
                <span className="font-semibold">{data.well.field_name}</span>
              </div>
              {totalSacks > 0 && (
                <div>
                  <span className="text-gray-600">Total Cement Placed:</span>{" "}
                  <span className="font-semibold">{totalSacks.toLocaleString()} sacks</span>
                </div>
              )}
              <div>
                <span className="text-gray-600">Jurisdiction:</span>{" "}
                <span className="font-semibold">{data.jurisdiction}</span>
              </div>
            </div>
          </div>

          {/* SVG Diagram */}
          <div className="flex justify-center">
            <svg ref={svgRef} width={svgWidth} height={propHeight ?? svgHeight}>
              <defs>
                {/* New cement (green) */}
                <pattern id="cementPattern" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
                  <rect width="10" height="10" fill="#22c55e" />
                  <circle cx="3" cy="3" r="1.5" fill="#000" />
                  <circle cx="8" cy="7" r="1" fill="#000" />
                  <circle cx="5" cy="9" r="0.8" fill="#000" />
                </pattern>
                {/* Existing cement (gray) */}
                <pattern id="existingCementPattern" width="12" height="12" patternUnits="userSpaceOnUse">
                  <rect width="12" height="12" fill="#9CA3AF" />
                  <circle cx="3" cy="3" r="1.5" fill="#000" opacity="0.4" />
                  <circle cx="8" cy="7" r="1.5" fill="#000" opacity="0.4" />
                  <circle cx="5" cy="9" r="1" fill="#000" opacity="0.3" />
                </pattern>
              </defs>

              {/* 1. Ground line */}
              <line
                x1={wellCenterX - 130}
                y1={depthToY(0)}
                x2={wellCenterX + 130}
                y2={depthToY(0)}
                stroke="#8B4513"
                strokeWidth="4"
              />
              <text
                x={5}
                y={depthToY(0) + 5}
                fontSize="11"
                fill="#666"
                fontWeight="bold"
                fontFamily="monospace"
              >
                0' Surface
              </text>

              {/* Wellhead symbol at surface — T-shaped cap spanning outermost casing */}
              {(() => {
                const widestCasing = sortedCasing[0]
                if (!widestCasing) return null
                const holeRadius = inchesToPixels(widestCasing.hole_size_in || widestCasing.size_in * 1.3) / 2
                const wellheadWidth = holeRadius + 15
                const groundY = depthToY(0)
                return (
                  <g>
                    {/* Horizontal cap bar */}
                    <rect
                      x={wellCenterX - wellheadWidth}
                      y={groundY - 6}
                      width={wellheadWidth * 2}
                      height={4}
                      fill="#374151"
                      rx="1"
                    />
                    {/* Left stub */}
                    <rect
                      x={wellCenterX - wellheadWidth}
                      y={groundY - 12}
                      width={4}
                      height={10}
                      fill="#374151"
                    />
                    {/* Right stub */}
                    <rect
                      x={wellCenterX + wellheadWidth - 4}
                      y={groundY - 12}
                      width={4}
                      height={10}
                      fill="#374151"
                    />
                  </g>
                )
              })()}

              {/* 2. Depth markers */}
              {Array.from({ length: Math.floor(maxDepth / tickInterval) + 1 }).map((_, i) => {
                const depth = i * tickInterval
                if (depth === 0) return null
                if (depth > maxDepth) return null
                const y = depthToY(depth)
                return (
                  <g key={`depth-marker-${depth}`}>
                    <line
                      x1={0}
                      y1={y}
                      x2={10}
                      y2={y}
                      stroke="#999"
                      strokeWidth="1"
                    />
                    <text
                      x={12}
                      y={y + 3}
                      fontSize="9"
                      fill="#999"
                      fontFamily="monospace"
                    >
                      {depth.toLocaleString()}'
                    </text>
                  </g>
                )
              })}

              {/* 3. Hole sections (dotted) */}
              {sortedCasing.map((casing, idx) => {
                const startDepth = idx === 0 ? 0 : sortedCasing[idx - 1].bottom_ft
                const holeWidth = inchesToPixels(casing.hole_size_in || casing.size_in * 1.3)
                return (
                  <g key={`hole-${idx}`}>
                    <line
                      x1={wellCenterX - holeWidth / 2}
                      y1={depthToY(startDepth)}
                      x2={wellCenterX - holeWidth / 2}
                      y2={depthToY(casing.bottom_ft)}
                      stroke="#999"
                      strokeWidth="2"
                      strokeDasharray="5,5"
                    />
                    <line
                      x1={wellCenterX + holeWidth / 2}
                      y1={depthToY(startDepth)}
                      x2={wellCenterX + holeWidth / 2}
                      y2={depthToY(casing.bottom_ft)}
                      stroke="#999"
                      strokeWidth="2"
                      strokeDasharray="5,5"
                    />
                  </g>
                )
              })}

              {/* 4. Existing cement in casing annulus (gray) */}
              {visibility.existingCement && sortedCasing.map((casing, idx) => {
                // Skip if no cement data — null means unknown, not surface-to-shoe
                if (casing.cement_top_ft == null) return null
                const cementTopFt = casing.cement_top_ft
                const cementBottomFt = casing.bottom_ft
                if (cementTopFt >= cementBottomFt) return null

                // Only draw cement for the outermost casing (against open hole), not between casing strings
                const outerCasing = idx > 0 ? sortedCasing[idx - 1] : null
                if (outerCasing) return null

                const innerRadius = inchesToPixels(casing.size_in) / 2
                const holeRadius = casing.hole_size_in ? inchesToPixels(casing.hole_size_in) / 2 : null
                if (!holeRadius || holeRadius <= innerRadius) return null

                const segments: Array<{ top: number; bottom: number; outer: number }> = [
                  { top: cementTopFt, bottom: cementBottomFt, outer: holeRadius },
                ]

                return (
                  <g key={`existing-cement-${idx}`}>
                    {segments.map((seg, segIdx) => {
                      const segTopY = depthToY(seg.top)
                      const segBottomY = depthToY(seg.bottom)
                      const segHeight = Math.max(2, segBottomY - segTopY)
                      return (
                        <g key={`cement-seg-${idx}-${segIdx}`}>
                          <rect
                            x={wellCenterX - seg.outer}
                            y={segTopY}
                            width={seg.outer * 2}
                            height={segHeight}
                            fill="url(#existingCementPattern)"
                            opacity="0.8"
                          />
                          <rect
                            x={wellCenterX - innerRadius}
                            y={segTopY}
                            width={innerRadius * 2}
                            height={segHeight}
                            fill="white"
                          />
                        </g>
                      )
                    })}
                  </g>
                )
              })}


              {/* 4c. Existing downhole tools — distinct visual patterns per tool type */}
              {visibility.existingTools && (() => {
                const allTools = [
                  ...existingTools,
                  ...mechanicalEquipment
                ]
                const uniqueTools = allTools.filter((tool: any, index: number, self: any[]) => {
                  const toolDepth = tool.depth_ft ?? tool.top_ft
                  const toolType = tool.type || tool.tool_type
                  return index === self.findIndex((t: any) =>
                    (t.depth_ft ?? t.top_ft) === toolDepth && (t.type || t.tool_type) === toolType
                  )
                })

                return uniqueTools.map((tool: any, idx: number) => {
                  const depth = tool.depth_ft ?? tool.top_ft ?? tool.bottom_ft
                  if (depth == null) return null
                  const y = depthToY(depth)
                  const toolType = (tool.tool_type || tool.type || '').toUpperCase()
                  const labelX = 5

                  let casingSize = sortedCasing[sortedCasing.length - 1]?.size_in ?? 7
                  for (const casing of sortedCasing) {
                    if (depth <= casing.bottom_ft) {
                      casingSize = casing.size_in
                      break
                    }
                  }
                  const boxWidth = inchesToPixels(casingSize)
                  const boxHeight = 20

                  // --- CIBP / Bridge Plug: amber fill, X cross pattern ---
                  if (toolType.includes('CIBP') || toolType.includes('BRIDGE')) {
                    const fillColor = '#FEF3C7'
                    const strokeColor = '#92400E'
                    return (
                      <g key={`tool-${idx}`}>
                        <rect
                          x={wellCenterX - boxWidth / 2}
                          y={y - boxHeight}
                          width={boxWidth}
                          height={boxHeight}
                          fill={fillColor}
                          stroke={strokeColor}
                          strokeWidth="2"
                          strokeDasharray="3,3"
                        />
                        <line
                          x1={wellCenterX - boxWidth / 2 + 2}
                          y1={y - boxHeight + 2}
                          x2={wellCenterX + boxWidth / 2 - 2}
                          y2={y - 2}
                          stroke={strokeColor}
                          strokeWidth="2"
                        />
                        <line
                          x1={wellCenterX + boxWidth / 2 - 2}
                          y1={y - boxHeight + 2}
                          x2={wellCenterX - boxWidth / 2 + 2}
                          y2={y - 2}
                          stroke={strokeColor}
                          strokeWidth="2"
                        />
                        <text x={labelX} y={y + 4} fontSize="9" fill={strokeColor} fontWeight="600">
                          {tool.tool_type || tool.type || 'Tool'} @ {depth.toLocaleString()}'
                        </text>
                      </g>
                    )
                  }

                  // --- Packer: blue fill, 3 thick horizontal bars ---
                  if (toolType.includes('PACKER') && !toolType.includes('STRADDLE')) {
                    const fillColor = '#DBEAFE'
                    const strokeColor = '#1D4ED8'
                    const midY = y - boxHeight / 2
                    return (
                      <g key={`tool-${idx}`}>
                        <rect
                          x={wellCenterX - boxWidth / 2}
                          y={y - boxHeight}
                          width={boxWidth}
                          height={boxHeight}
                          fill={fillColor}
                          stroke={strokeColor}
                          strokeWidth="2"
                          strokeDasharray="4,2"
                        />
                        <line x1={wellCenterX - boxWidth / 2 + 2} y1={y - boxHeight + 4} x2={wellCenterX + boxWidth / 2 - 2} y2={y - boxHeight + 4} stroke={strokeColor} strokeWidth="3" />
                        <line x1={wellCenterX - boxWidth / 2 + 2} y1={midY} x2={wellCenterX + boxWidth / 2 - 2} y2={midY} stroke={strokeColor} strokeWidth="3" />
                        <line x1={wellCenterX - boxWidth / 2 + 2} y1={y - 4} x2={wellCenterX + boxWidth / 2 - 2} y2={y - 4} stroke={strokeColor} strokeWidth="3" />
                        <text x={labelX} y={y + 4} fontSize="9" fill={strokeColor} fontWeight="600">
                          {tool.tool_type || tool.type || 'Tool'} @ {depth.toLocaleString()}'
                        </text>
                      </g>
                    )
                  }

                  // --- Cement Retainer / RETAINER: purple fill, filled inverted-V triangle ---
                  if (toolType.includes('RETAINER')) {
                    const fillColor = '#F3E8FF'
                    const strokeColor = '#7C3AED'
                    const cx = wellCenterX
                    const triTop = y - boxHeight + 3
                    const triBottom = y - 3
                    const halfBase = boxWidth / 2 - 3
                    const triPoints = `${cx},${triTop} ${cx + halfBase},${triBottom} ${cx - halfBase},${triBottom}`
                    return (
                      <g key={`tool-${idx}`}>
                        <rect
                          x={wellCenterX - boxWidth / 2}
                          y={y - boxHeight}
                          width={boxWidth}
                          height={boxHeight}
                          fill={fillColor}
                          stroke={strokeColor}
                          strokeWidth="2"
                          strokeDasharray="4,2"
                        />
                        <polygon
                          points={triPoints}
                          fill={strokeColor}
                          opacity="0.7"
                        />
                        <text x={labelX} y={y + 4} fontSize="9" fill={strokeColor} fontWeight="600">
                          {tool.tool_type || tool.type || 'Tool'} @ {depth.toLocaleString()}'
                        </text>
                      </g>
                    )
                  }

                  // --- DV Tool: teal fill, diamond shape ---
                  if (toolType.includes('DV')) {
                    const fillColor = '#CCFBF1'
                    const strokeColor = '#0F766E'
                    const cx = wellCenterX
                    const midY = y - boxHeight / 2
                    const halfW = boxWidth / 2 - 3
                    const halfH = boxHeight / 2 - 2
                    const diamondPoints = `${cx},${midY - halfH} ${cx + halfW},${midY} ${cx},${midY + halfH} ${cx - halfW},${midY}`
                    return (
                      <g key={`tool-${idx}`}>
                        <rect
                          x={wellCenterX - boxWidth / 2}
                          y={y - boxHeight}
                          width={boxWidth}
                          height={boxHeight}
                          fill={fillColor}
                          stroke={strokeColor}
                          strokeWidth="2"
                          strokeDasharray="3,3"
                        />
                        <polygon
                          points={diamondPoints}
                          fill={strokeColor}
                          opacity="0.75"
                        />
                        <text x={labelX} y={y + 4} fontSize="9" fill={strokeColor} fontWeight="600">
                          {tool.tool_type || tool.type || 'Tool'} @ {depth.toLocaleString()}'
                        </text>
                      </g>
                    )
                  }

                  // --- Straddle Packer: orange fill, double set of horizontal bars ---
                  if (toolType.includes('STRADDLE')) {
                    const fillColor = '#FED7AA'
                    const strokeColor = '#C2410C'
                    return (
                      <g key={`tool-${idx}`}>
                        <rect
                          x={wellCenterX - boxWidth / 2}
                          y={y - boxHeight}
                          width={boxWidth}
                          height={boxHeight}
                          fill={fillColor}
                          stroke={strokeColor}
                          strokeWidth="2"
                          strokeDasharray="5,2"
                        />
                        <line x1={wellCenterX - boxWidth / 2 + 2} y1={y - boxHeight + 3} x2={wellCenterX + boxWidth / 2 - 2} y2={y - boxHeight + 3} stroke={strokeColor} strokeWidth="2" />
                        <line x1={wellCenterX - boxWidth / 2 + 2} y1={y - boxHeight + 6} x2={wellCenterX + boxWidth / 2 - 2} y2={y - boxHeight + 6} stroke={strokeColor} strokeWidth="2" />
                        <line x1={wellCenterX - boxWidth / 2 + 2} y1={y - boxHeight + 9} x2={wellCenterX + boxWidth / 2 - 2} y2={y - boxHeight + 9} stroke={strokeColor} strokeWidth="2" />
                        <line x1={wellCenterX - boxWidth / 2 + 2} y1={y - 9} x2={wellCenterX + boxWidth / 2 - 2} y2={y - 9} stroke={strokeColor} strokeWidth="2" />
                        <line x1={wellCenterX - boxWidth / 2 + 2} y1={y - 6} x2={wellCenterX + boxWidth / 2 - 2} y2={y - 6} stroke={strokeColor} strokeWidth="2" />
                        <line x1={wellCenterX - boxWidth / 2 + 2} y1={y - 3} x2={wellCenterX + boxWidth / 2 - 2} y2={y - 3} stroke={strokeColor} strokeWidth="2" />
                        <text x={labelX} y={y + 4} fontSize="9" fill={strokeColor} fontWeight="600">
                          {tool.tool_type || tool.type || 'Tool'} @ {depth.toLocaleString()}'
                        </text>
                      </g>
                    )
                  }

                  // --- Fallback: generic tool with 3 horizontal ticks ---
                  const fillColor = '#F3F4F6'
                  const strokeColor = '#6B7280'
                  return (
                    <g key={`tool-${idx}`}>
                      <rect
                        x={wellCenterX - boxWidth / 2}
                        y={y - boxHeight}
                        width={boxWidth}
                        height={boxHeight}
                        fill={fillColor}
                        stroke={strokeColor}
                        strokeWidth="2"
                        strokeDasharray="3,3"
                      />
                      <line x1={wellCenterX - boxWidth / 2 + 2} y1={y - 5} x2={wellCenterX + boxWidth / 2 - 2} y2={y - 5} stroke={strokeColor} strokeWidth="2" />
                      <line x1={wellCenterX - boxWidth / 2 + 2} y1={y} x2={wellCenterX + boxWidth / 2 - 2} y2={y} stroke={strokeColor} strokeWidth="2" />
                      <line x1={wellCenterX - boxWidth / 2 + 2} y1={y + 5} x2={wellCenterX + boxWidth / 2 - 2} y2={y + 5} stroke={strokeColor} strokeWidth="2" />
                      <text x={labelX} y={y + 4} fontSize="9" fill={strokeColor} fontWeight="600">
                        {tool.tool_type || tool.type || 'Tool'} @ {depth.toLocaleString()}'
                      </text>
                    </g>
                  )
                })
              })()}

              {/* 4d. Tubing */}
              {visibility.tubing && tubing.map((t: { top_ft: number | null; size_in: number; bottom_ft: number }, idx: number) => {
                const tubingWidth = inchesToPixels(t.size_in || 2.875)
                const topY = depthToY(t.top_ft || 0)
                const bottomY = depthToY(t.bottom_ft)
                return (
                  <g key={`tubing-${idx}`}>
                    <line x1={wellCenterX - tubingWidth / 2} y1={topY} x2={wellCenterX - tubingWidth / 2} y2={bottomY} stroke="#F59E0B" strokeWidth="2" strokeDasharray="3,3" />
                    <line x1={wellCenterX + tubingWidth / 2} y1={topY} x2={wellCenterX + tubingWidth / 2} y2={bottomY} stroke="#F59E0B" strokeWidth="2" strokeDasharray="3,3" />
                  </g>
                )
              })}

              {/* 4e. Liner */}
              {liner.map((l: any, idx: number) => {
                const linerWidth = inchesToPixels(l.size_in || 4.5)
                const hangerY = depthToY(l.top_ft || 0)
                const bottomY = depthToY(l.bottom_ft)
                const linerLeftX = wellCenterX - linerWidth / 2
                const linerRightX = wellCenterX + linerWidth / 2
                const labelX = linerLeftX - 8
                return (
                  <g key={`liner-${idx}`}>
                    <line x1={linerLeftX} y1={hangerY} x2={linerLeftX} y2={bottomY} stroke="#666" strokeWidth="2" strokeDasharray="12,4" />
                    <line x1={linerRightX} y1={hangerY} x2={linerRightX} y2={bottomY} stroke="#666" strokeWidth="2" strokeDasharray="12,4" />
                    {/* Liner hanger symbol — horizontal stubs at top of liner */}
                    <line x1={linerLeftX - 4} y1={hangerY} x2={linerLeftX + 2} y2={hangerY} stroke="#6B7280" strokeWidth="2" />
                    <line x1={linerRightX - 2} y1={hangerY} x2={linerRightX + 4} y2={hangerY} stroke="#6B7280" strokeWidth="2" />
                    {/* Liner top label */}
                    <text x={labelX} y={hangerY - 6} fontSize="10" fill="#6B7280" fontWeight="600" textAnchor="end">
                      Liner Top
                    </text>
                    <text x={labelX} y={hangerY + 8} fontSize="9" fill="#6B7280" textAnchor="end">
                      {(l.top_ft || 0).toLocaleString()}'
                    </text>
                  </g>
                )
              })}

              {/* 5. As-plugged cement plugs colored by deviation level */}
              {data.plugs.map((plug, plugIdx) => {
                const shallowerDepth = Math.min(plug.top_ft, plug.bottom_ft)
                const deeperDepth = Math.max(plug.top_ft, plug.bottom_ft)
                const topY = depthToY(shallowerDepth)
                const bottomY = depthToY(deeperDepth)
                const h = Math.abs(bottomY - topY) || 10

                // Find which casing this plug is in — use INNERMOST casing at this depth
                // sortedCasing is outermost-first (index 0 = largest), so search from the end
                let currentCasing = sortedCasing[sortedCasing.length - 1]
                for (let i = sortedCasing.length - 1; i >= 0; i--) {
                  if (shallowerDepth <= sortedCasing[i].bottom_ft) {
                    currentCasing = sortedCasing[i]
                    break
                  }
                }

                const interiorCementWidth = inchesToPixels(currentCasing?.size_in ?? 7)
                const cementWidth = interiorCementWidth
                const plugHeight = h

                // Bridge plug / mechanical plug: yellow X-hatched box
                if (plug.type === 'bridge_plug' || plug.type === 'mechanical_plug') {
                  if (!visibility.bridgePlugs) return null
                  const boxHeight = 20
                  const boxWidth = inchesToPixels(currentCasing?.size_in ?? 7)
                  const y = depthToY(plug.top_ft)
                  return (
                    <g key={`plug-${plug.plug_number}-${plugIdx}`}>
                      <rect
                        x={wellCenterX - boxWidth / 2}
                        y={y - boxHeight}
                        width={boxWidth}
                        height={boxHeight}
                        fill="#FCD34D"
                        stroke="#000"
                        strokeWidth="2"
                      />
                      <line
                        x1={wellCenterX - boxWidth / 2 + 2}
                        y1={y - boxHeight + 2}
                        x2={wellCenterX + boxWidth / 2 - 2}
                        y2={y - 2}
                        stroke="#000"
                        strokeWidth="3"
                      />
                      <line
                        x1={wellCenterX + boxWidth / 2 - 2}
                        y1={y - boxHeight + 2}
                        x2={wellCenterX - boxWidth / 2 + 2}
                        y2={y - 2}
                        stroke="#000"
                        strokeWidth="3"
                      />
                    </g>
                  )
                }

                // Existing cement: amber dashed style
                if (plug.type === 'existing_cement') {
                  if (!visibility.newCementPlugs) return null
                  return (
                    <g key={`plug-${plug.plug_number}-${plugIdx}`}>
                      <rect
                        x={wellCenterX - cementWidth / 2}
                        y={depthToY(plug.top_ft)}
                        width={cementWidth}
                        height={plugHeight}
                        fill="#D97706"
                        opacity={0.4}
                        stroke="#92400E"
                        strokeWidth={2}
                        strokeDasharray="6,3"
                      />
                    </g>
                  )
                }

                // Perf + squeeze/circulate: annular cement behind casing + interior cement on top
                // Both squeeze and circulate pump cement into the annulus behind casing
                const isPS = plug.plug_type === 'perf_and_squeeze_plug' ||
                  plug.plug_type === 'perf_and_circulate_plug' ||
                  plug.type === 'squeeze' ||
                  plug.placement_method?.includes('squeeze') ||
                  plug.placement_method?.includes('circulate') ||
                  (plug.placement_method?.includes('perf') && plug.placement_method?.includes('squeeze')) ||
                  (plug.placement_method?.includes('perf') && plug.placement_method?.includes('circ'))

                if (isPS) {
                  if (!visibility.newCementPlugs) return null
                  const annulusData = plug.details?.annuli?.[0]
                  const casingIdx = sortedCasing.findIndex(c => c === currentCasing)
                  // Outer boundary: annuli data → next outer casing ID → hole size → fallback
                  let outerID = annulusData?.outer_id_in
                  if (!outerID && casingIdx > 0) {
                    // Use next outer casing's OD — cement fills right up to the casing wall
                    outerID = sortedCasing[casingIdx - 1].size_in
                  } else if (!outerID) {
                    outerID = currentCasing?.hole_size_in ?? (currentCasing?.size_in ?? 5.5) * 1.45
                  }
                  const annulusOuterRadius = inchesToPixels(outerID) / 2
                  const casingIDRadius = inchesToPixels((currentCasing?.size_in ?? 5.5) * 0.87) / 2

                  // Check casing cement_top_ft — skip annular cement if the annulus is already cemented
                  let skipAnnularCement = false
                  if (!skipAnnularCement) {
                    if (currentCasing?.cement_top_ft != null) {
                      const cementTop = currentCasing.cement_top_ft
                      const cementBottom = currentCasing.bottom_ft
                      if (shallowerDepth >= cementTop && deeperDepth <= cementBottom) {
                        skipAnnularCement = true
                      }
                    }
                    // Note: outer casing's cement_top_ft describes cement OUTSIDE that casing,
                    // not the annulus being filled here. Only the plug's own casing cement_top_ft matters.
                  }

                  return (
                    <g key={`plug-${plug.plug_number}-${plugIdx}`}>
                      {/* Annular cement for perf+squeeze (behind pipe) — only if annulus not already cemented */}
                      {!skipAnnularCement && (
                        <>
                          <rect
                            x={wellCenterX - annulusOuterRadius}
                            y={depthToY(plug.top_ft)}
                            width={annulusOuterRadius * 2}
                            height={depthToY(plug.bottom_ft) - depthToY(plug.top_ft)}
                            fill="url(#cementPattern)"
                            opacity={0.8}
                          />
                          {/* Inner cutout (white, preserves casing interior) */}
                          <rect
                            x={wellCenterX - casingIDRadius}
                            y={depthToY(plug.top_ft) - 1}
                            width={casingIDRadius * 2}
                            height={depthToY(plug.bottom_ft) - depthToY(plug.top_ft) + 2}
                            fill="white"
                          />
                        </>
                      )}
                      {/* Interior cement on top */}
                      <rect
                        x={wellCenterX - interiorCementWidth / 2}
                        y={topY}
                        width={interiorCementWidth}
                        height={h}
                        fill="url(#cementPattern)"
                        stroke="#22C55E"
                        strokeWidth="2"
                      />
                    </g>
                  )
                }

                // Default: standard interior cement plug
                if (!visibility.newCementPlugs) return null
                return (
                  <g key={`plug-${plug.plug_number}-${plugIdx}`}>
                    <rect
                      x={wellCenterX - interiorCementWidth / 2}
                      y={topY}
                      width={interiorCementWidth}
                      height={h}
                      fill="url(#cementPattern)"
                      stroke="#22C55E"
                      strokeWidth="2"
                    />
                  </g>
                )
              })}

              {/* 6. Tagged depth markers (diamond) */}
              {data.plugs
                .filter(p => p.tagged_depth_ft != null)
                .map((plug, idx) => {
                  const tagY = depthToY(plug.tagged_depth_ft!)
                  const color = '#22c55e'
                  const stroke = '#16a34a'
                  const dSize = 6
                  return (
                    <g key={`tag-${plug.plug_number}-${idx}`}>
                      {/* Diamond shape */}
                      <polygon
                        points={`
                          ${wellCenterX},${tagY - dSize}
                          ${wellCenterX + dSize},${tagY}
                          ${wellCenterX},${tagY + dSize}
                          ${wellCenterX - dSize},${tagY}
                        `}
                        fill={color}
                        stroke={stroke}
                        strokeWidth="1.5"
                      />
                    </g>
                  )
                })}

              {/* 7. Casing strings */}
              {visibility.casing && sortedCasing.map((casing, idx) => {
                const casingWidth = inchesToPixels(casing.size_in)
                const nextCasing = sortedCasing[idx + 1]
                const cutDepth = casing.removed_to_depth_ft
                const isCut = cutDepth != null && cutDepth > 0
                const topY = depthToY(casing.top_ft || 0)
                const bottomY = depthToY(casing.bottom_ft)
                const shoeY = bottomY
                const cutY = isCut ? depthToY(cutDepth!) : null
                const leftX = wellCenterX - casingWidth / 2
                const rightX = wellCenterX + casingWidth / 2

                return (
                  <g key={`casing-${idx}`}>
                    {/* Ghost/dashed section from top down to cut depth (pulled casing) */}
                    {isCut && (
                      <>
                        <line
                          x1={leftX}
                          y1={topY}
                          x2={leftX}
                          y2={cutY!}
                          stroke="#333"
                          strokeWidth="3"
                          strokeDasharray="6,4"
                          opacity="0.3"
                        />
                        <line
                          x1={rightX}
                          y1={topY}
                          x2={rightX}
                          y2={cutY!}
                          stroke="#333"
                          strokeWidth="3"
                          strokeDasharray="6,4"
                          opacity="0.3"
                        />
                        {/* Red X cut mark at cut depth */}
                        <line
                          x1={leftX - 4}
                          y1={cutY! - 6}
                          x2={rightX + 4}
                          y2={cutY! + 6}
                          stroke="#DC2626"
                          strokeWidth="2.5"
                        />
                        <line
                          x1={rightX + 4}
                          y1={cutY! - 6}
                          x2={leftX - 4}
                          y2={cutY! + 6}
                          stroke="#DC2626"
                          strokeWidth="2.5"
                        />
                        {/* Cut depth label */}
                        <text
                          x={rightX + 8}
                          y={cutY! + 4}
                          fontSize="9"
                          fill="#DC2626"
                          fontWeight="600"
                        >
                          {casing.size_in}&quot; cut @ {cutDepth!.toLocaleString()}&apos;
                        </text>
                      </>
                    )}

                    {/* Solid casing lines — from cut depth (or top) down to bottom */}
                    <line
                      x1={leftX}
                      y1={isCut ? cutY! : topY}
                      x2={leftX}
                      y2={bottomY}
                      stroke="#333"
                      strokeWidth="3"
                    />
                    <line
                      x1={rightX}
                      y1={isCut ? cutY! : topY}
                      x2={rightX}
                      y2={bottomY}
                      stroke="#333"
                      strokeWidth="3"
                    />

                    {nextCasing && (
                      <>
                        <line
                          x1={leftX}
                          y1={depthToY(casing.bottom_ft)}
                          x2={wellCenterX - inchesToPixels(nextCasing.size_in) / 2}
                          y2={depthToY(casing.bottom_ft)}
                          stroke="#333"
                          strokeWidth="3"
                        />
                        <line
                          x1={wellCenterX + inchesToPixels(nextCasing.size_in) / 2}
                          y1={depthToY(casing.bottom_ft)}
                          x2={rightX}
                          y2={depthToY(casing.bottom_ft)}
                          stroke="#333"
                          strokeWidth="3"
                        />
                      </>
                    )}
                    {!nextCasing && (
                      <line
                        x1={leftX}
                        y1={depthToY(casing.bottom_ft)}
                        x2={rightX}
                        y2={depthToY(casing.bottom_ft)}
                        stroke="#333"
                        strokeWidth="3"
                      />
                    )}

                    {/* Casing shoe — right triangles between outermost casing OD and open hole */}
                    {(() => {
                      const outerCasingForShoe = idx > 0 ? sortedCasing[idx - 1] : null
                      if (outerCasingForShoe) return null  // inner casings: no shoe against open hole
                      if (!casing.hole_size_in) return null
                      const outerR = inchesToPixels(casing.hole_size_in) / 2
                      if (outerR <= casingWidth / 2) return null
                      const innerR = casingWidth / 2
                      const shoeH = 10
                      return (
                        <>
                          <polygon
                            points={`${wellCenterX - innerR},${shoeY - shoeH} ${wellCenterX - innerR},${shoeY} ${wellCenterX - outerR},${shoeY}`}
                            fill="#333"
                            stroke="#333"
                            strokeWidth="1"
                          />
                          <polygon
                            points={`${wellCenterX + innerR},${shoeY - shoeH} ${wellCenterX + innerR},${shoeY} ${wellCenterX + outerR},${shoeY}`}
                            fill="#333"
                            stroke="#333"
                            strokeWidth="1"
                          />
                        </>
                      )
                    })()}
                  </g>
                )
              })}

              {/* 8. Labels with connectors */}
              {adjustedLabels.map((label, idx) => {
                const outerCasingWidth = inchesToPixels(sortedCasing[0]?.hole_size_in || sortedCasing[0]?.size_in || 10)
                const connectorStartX = wellCenterX + outerCasingWidth / 2 + 10
                return (
                  <g key={`label-${idx}`}>
                    <line
                      x1={connectorStartX}
                      y1={label.y}
                      x2={labelStartX - 5}
                      y2={label.adjustedY}
                      stroke="#9CA3AF"
                      strokeWidth="1"
                    />
                    <circle cx={connectorStartX} cy={label.y} r={2} fill={label.color} />
                    <text x={labelStartX} y={label.adjustedY} fontSize="11" fill={label.color} fontWeight="600">
                      {label.text}
                    </text>
                    {label.subtext && label.subtext.split('\n').map((line, li) => (
                      <text
                        key={li}
                        x={labelStartX}
                        y={label.adjustedY + 14 + li * 13}
                        fontSize="10"
                        fill={label.color}
                      >
                        {line}
                      </text>
                    ))}
                  </g>
                )
              })}

              {/* 9. Formation tops */}
              {visibility.formationTops && formationTops.length > 0 && (() => {
                const depthMap = new Map<number, string[]>()
                // Only show formation tops within the well's total depth
                formationTops
                  .filter((f: FormationTop) => f.top_ft != null && f.top_ft <= maxDepth)
                  .forEach((f: FormationTop) => {
                  if (f.top_ft != null) {
                    if (!depthMap.has(f.top_ft)) depthMap.set(f.top_ft, [])
                    depthMap.get(f.top_ft)!.push(f.formation)
                  }
                })
                const sortedFormations = Array.from(depthMap.entries()).sort((a, b) => a[0] - b[0])

                // Compute adjusted label positions to avoid overlap
                const minFormationGap = 25 // Minimum pixels between formation labels
                const formationLabelPositions: Array<{ depth: number; names: string[]; trueY: number; adjustedY: number }> = []

                sortedFormations.forEach(([depth, formationNames], idx) => {
                  const trueY = depthToY(depth)
                  if (idx === 0) {
                    formationLabelPositions.push({ depth, names: formationNames, trueY, adjustedY: trueY })
                  } else {
                    const prevAdjustedY = formationLabelPositions[idx - 1].adjustedY
                    const adjustedY = trueY - prevAdjustedY < minFormationGap ? prevAdjustedY + minFormationGap : trueY
                    formationLabelPositions.push({ depth, names: formationNames, trueY, adjustedY })
                  }
                })

                const labelX = wellCenterX - inchesToPixels(sortedCasing[0]?.hole_size_in || sortedCasing[0]?.size_in || 10) / 2 - 5
                const lineEndX = wellCenterX + inchesToPixels(sortedCasing[0]?.hole_size_in || sortedCasing[0]?.size_in || 10) / 2 + 5

                return formationLabelPositions.map((item, idx) => {
                  const uniqueFormations = Array.from(new Set(item.names))
                  const combinedLabel = uniqueFormations.join('/')
                  const fColors = getFormationColors(uniqueFormations[0])
                  const isOffset = Math.abs(item.adjustedY - item.trueY) > 2

                  return (
                    <g key={`formation-${idx}`}>
                      {/* Formation zone dashed line at TRUE depth */}
                      <line
                        x1={0}
                        y1={item.trueY}
                        x2={lineEndX}
                        y2={item.trueY}
                        stroke={fColors.border}
                        strokeWidth="2"
                        strokeDasharray="5,5"
                      />

                      {/* Connector from true depth to adjusted label position */}
                      {isOffset && (
                        <line
                          x1={labelX + 5}
                          y1={item.trueY}
                          x2={labelX + 5}
                          y2={item.adjustedY - 7}
                          stroke={fColors.border}
                          strokeWidth="1"
                          opacity="0.5"
                        />
                      )}

                      {/* Formation name at ADJUSTED position */}
                      <text
                        x={labelX}
                        y={item.adjustedY - 5}
                        fontSize="11"
                        fill={fColors.text}
                        fontWeight="600"
                        textAnchor="end"
                      >
                        {combinedLabel}
                      </text>

                      {/* Depth value at ADJUSTED position */}
                      <text
                        x={labelX}
                        y={item.adjustedY + 10}
                        fontSize="10"
                        fill={fColors.text}
                        textAnchor="end"
                      >
                        {item.depth.toLocaleString()}'
                      </text>
                    </g>
                  )
                })
              })()}

              {/* 10. Perforations - orange triangles matching ProposedPlugWBD style */}
              {visibility.perforations && sortedCasing.length > 0 && productionPerforations.map((perf: { top_ft: number; bottom_ft: number }, idx: number) => {
                const topY = depthToY(perf.top_ft)
                const bottomY = depthToY(perf.bottom_ft)
                // Find the innermost casing at this depth
                let casingWidth = inchesToPixels(sortedCasing[sortedCasing.length - 1].size_in)
                for (let i = sortedCasing.length - 1; i >= 0; i--) {
                  if (perf.top_ft <= sortedCasing[i].bottom_ft) {
                    casingWidth = inchesToPixels(sortedCasing[i].size_in)
                    break
                  }
                }

                const triangleBase = 5
                const triangleHeight = 20
                const startRadius = (casingWidth / 2) - 2

                return (
                  <g key={`perf-${idx}`}>
                    {/* Top depth - left triangle pointing outward */}
                    <polygon
                      points={`
                        ${wellCenterX - startRadius},${topY - triangleBase / 2}
                        ${wellCenterX - startRadius},${topY + triangleBase / 2}
                        ${wellCenterX - startRadius - triangleHeight},${topY}
                      `}
                      fill="#F97316"
                      stroke="#D97706"
                      strokeWidth="1"
                      opacity="0.9"
                    />
                    {/* Top depth - right triangle pointing outward */}
                    <polygon
                      points={`
                        ${wellCenterX + startRadius},${topY - triangleBase / 2}
                        ${wellCenterX + startRadius},${topY + triangleBase / 2}
                        ${wellCenterX + startRadius + triangleHeight},${topY}
                      `}
                      fill="#F97316"
                      stroke="#D97706"
                      strokeWidth="1"
                      opacity="0.9"
                    />
                    {/* Bottom depth - left triangle pointing outward */}
                    <polygon
                      points={`
                        ${wellCenterX - startRadius},${bottomY - triangleBase / 2}
                        ${wellCenterX - startRadius},${bottomY + triangleBase / 2}
                        ${wellCenterX - startRadius - triangleHeight},${bottomY}
                      `}
                      fill="#F97316"
                      stroke="#D97706"
                      strokeWidth="1"
                      opacity="0.9"
                    />
                    {/* Bottom depth - right triangle pointing outward */}
                    <polygon
                      points={`
                        ${wellCenterX + startRadius},${bottomY - triangleBase / 2}
                        ${wellCenterX + startRadius},${bottomY + triangleBase / 2}
                        ${wellCenterX + startRadius + triangleHeight},${bottomY}
                      `}
                      fill="#F97316"
                      stroke="#D97706"
                      strokeWidth="1"
                      opacity="0.9"
                    />
                    {/* Left side vertical connector between top and bottom */}
                    <line
                      x1={wellCenterX - startRadius - triangleHeight / 2}
                      y1={topY}
                      x2={wellCenterX - startRadius - triangleHeight / 2}
                      y2={bottomY}
                      stroke="#F97316"
                      strokeWidth="1.5"
                      strokeDasharray="4,3"
                      opacity="0.7"
                    />
                    {/* Right side vertical connector between top and bottom */}
                    <line
                      x1={wellCenterX + startRadius + triangleHeight / 2}
                      y1={topY}
                      x2={wellCenterX + startRadius + triangleHeight / 2}
                      y2={bottomY}
                      stroke="#F97316"
                      strokeWidth="1.5"
                      strokeDasharray="4,3"
                      opacity="0.7"
                    />
                  </g>
                )
              })}

              {/* KOP (Kickoff Point) marker */}
              {wellGeometry.kop_ft != null && (() => {
                const kopY = depthToY(wellGeometry.kop_ft!)
                const outerHoleSize = sortedCasing[0]?.hole_size_in || sortedCasing[0]?.size_in || 10
                const lineEndX = wellCenterX + inchesToPixels(outerHoleSize) / 2 + 5
                const labelX = wellCenterX - inchesToPixels(outerHoleSize) / 2 - 5
                return (
                  <g key="kop-marker">
                    <line x1={0} y1={kopY} x2={lineEndX} y2={kopY} stroke="#7C3AED" strokeWidth="2" strokeDasharray="8,4" />
                    <text x={labelX} y={kopY - 5} fontSize="11" fill="#7C3AED" fontWeight="700" textAnchor="end">KOP</text>
                    <text x={labelX} y={kopY + 10} fontSize="10" fill="#7C3AED" textAnchor="end">
                      {wellGeometry.kop_ft!.toLocaleString()}'
                    </text>
                  </g>
                )
              })()}

              {/* Open hole section below deepest casing shoe */}
              {(() => {
                if (sortedCasing.length === 0) return null
                const deepestShoe = Math.max(...sortedCasing.map((c: CasingString) => c.bottom_ft || 0))
                if (maxDepth <= deepestShoe + 50) return null
                const shoeY = depthToY(deepestShoe)
                const tdY = depthToY(maxDepth)
                return (
                  <g>
                    <line
                      x1={wellCenterX - 8}
                      y1={shoeY}
                      x2={wellCenterX - 8}
                      y2={tdY}
                      stroke="#9CA3AF"
                      strokeWidth="1.5"
                      strokeDasharray="6,4"
                    />
                    <line
                      x1={wellCenterX + 8}
                      y1={shoeY}
                      x2={wellCenterX + 8}
                      y2={tdY}
                      stroke="#9CA3AF"
                      strokeWidth="1.5"
                      strokeDasharray="6,4"
                    />
                  </g>
                )
              })()}

              {/* 11. TD marker */}
              <line
                x1={wellCenterX - 130}
                y1={depthToY(maxDepth)}
                x2={wellCenterX + 130}
                y2={depthToY(maxDepth)}
                stroke="#e53e3e"
                strokeWidth="2"
              />
              <text
                x={5}
                y={depthToY(maxDepth) + 5}
                fontSize="11"
                fill="#e53e3e"
                fontWeight="bold"
                fontFamily="monospace"
              >
                TD {maxDepth.toLocaleString()}'
              </text>
            </svg>
          </div>

          {/* Summary Table */}
          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-gray-600">
                  <th className="py-2 px-3">Plug #</th>
                  <th className="py-2 px-3">Type</th>
                  <th className="py-2 px-3">Top (ft)</th>
                  <th className="py-2 px-3">Bottom (ft)</th>
                  <th className="py-2 px-3">Sacks</th>
                  <th className="py-2 px-3">Class</th>
                  <th className="py-2 px-3">Tagged Depth</th>
                  <th className="py-2 px-3">Method</th>
                  <th className="py-2 px-3">WOC (hrs)</th>
                  <th className="py-2 px-3">Tagged</th>
                </tr>
              </thead>
              <tbody>
                {data.plugs.map((plug, idx) => (
                  <tr key={`row-${plug.plug_number}-${idx}`} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-3 font-medium">{plug.plug_number}</td>
                    <td className="py-2 px-3 capitalize">{plug.type.replace(/_/g, ' ')}</td>
                    <td className="py-2 px-3">{plug.top_ft.toLocaleString()}</td>
                    <td className="py-2 px-3">{plug.bottom_ft.toLocaleString()}</td>
                    <td className="py-2 px-3">{plug.sacks.toLocaleString()}</td>
                    <td className="py-2 px-3">{plug.cement_class || '—'}</td>
                    <td className="py-2 px-3">
                      {plug.tagged_depth_ft != null ? `${plug.tagged_depth_ft.toLocaleString()} ft` : '—'}
                    </td>
                    <td className="py-2 px-3">
                      {plug.placement_method
                        ? plug.placement_method
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, c => c.toUpperCase())
                            .replace('And', '&')
                        : '—'}
                    </td>
                    <td className="py-2 px-3">{plug.woc_hours != null ? plug.woc_hours : '—'}</td>
                    <td className="py-2 px-3">
                      {plug.woc_tagged === true ? '✓' : plug.woc_tagged === false ? '✗' : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Casing Information Table */}
          <div className="mt-6">
            <h3 className="font-bold text-sm text-brand-900 mb-3">Casing Information</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Type</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Size (in)</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Hole Size (in)</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Top (ft)</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Bottom (ft)</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">TOC (ft)</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Shoe (ft)</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Casing strings */}
                  {sortedCasing.map((casing, idx) => (
                    <tr key={`casing-table-${idx}`} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                      <td className="px-3 py-2 text-gray-900 font-medium capitalize">{casing.string || 'Unknown'} Casing</td>
                      <td className="px-3 py-2 text-gray-700">{casing.size_in != null ? parseFloat(Number(casing.size_in).toFixed(3)) : "—"}"</td>
                      <td className="px-3 py-2 text-gray-700">{casing.hole_size_in != null ? parseFloat(Number(casing.hole_size_in).toFixed(3)) : "—"}"</td>
                      <td className="px-3 py-2 text-gray-700">{(casing.top_ft || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-gray-700">{(casing.bottom_ft ?? 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-gray-700">{casing.cement_top_ft != null ? casing.cement_top_ft.toLocaleString() : "—"}</td>
                      <td className="px-3 py-2 text-gray-700">{casing.bottom_ft != null ? casing.bottom_ft.toLocaleString() : "—"}</td>
                      <td className="px-3 py-2 text-gray-700">
                        {(casing as any).removed_to_depth_ft != null
                          ? `Cut @ ${(casing as any).removed_to_depth_ft.toLocaleString()}ft`
                          : 'In Hole'}
                      </td>
                    </tr>
                  ))}

                  {/* Tubing rows */}
                  {tubing.length > 0 && tubing.map((t: { top_ft: number | null; size_in: number; bottom_ft: number }, idx: number) => (
                    <tr key={`tubing-table-${idx}`} className={(sortedCasing.length + idx) % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                      <td className="px-3 py-2 text-gray-900 font-medium">Tubing</td>
                      <td className="px-3 py-2 text-gray-700">{t.size_in != null ? parseFloat(Number(t.size_in).toFixed(3)) : "—"}"</td>
                      <td className="px-3 py-2 text-gray-700">—</td>
                      <td className="px-3 py-2 text-gray-700">{(t.top_ft || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-gray-700">{(t.bottom_ft ?? 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-gray-700">—</td>
                      <td className="px-3 py-2 text-gray-700">—</td>
                      <td className="px-3 py-2 text-gray-700">In Hole</td>
                    </tr>
                  ))}

                  {/* Liner rows */}
                  {liner.length > 0 && liner.map((l: any, idx: number) => (
                    <tr key={`liner-table-${idx}`} className={(sortedCasing.length + tubing.length + idx) % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                      <td className="px-3 py-2 text-gray-900 font-medium">Liner</td>
                      <td className="px-3 py-2 text-gray-700">{l.size_in != null ? parseFloat(Number(l.size_in).toFixed(3)) : "—"}"</td>
                      <td className="px-3 py-2 text-gray-700">—</td>
                      <td className="px-3 py-2 text-gray-700">{(l.top_ft || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-gray-700">{l.bottom_ft != null ? l.bottom_ft.toLocaleString() : "—"}</td>
                      <td className="px-3 py-2 text-gray-700">{l.cement_top_ft != null ? l.cement_top_ft.toLocaleString() : "—"}</td>
                      <td className="px-3 py-2 text-gray-700">{l.bottom_ft != null ? l.bottom_ft.toLocaleString() : "—"}</td>
                      <td className="px-3 py-2 text-gray-700">
                        {l.removed_to_depth_ft != null
                          ? `Cut @ ${l.removed_to_depth_ft.toLocaleString()}ft`
                          : 'In Hole'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Formation Tops Table */}
          {formationTops.length > 0 && (
            <div className="mt-6">
              <h3 className="font-bold text-sm text-brand-900 mb-3">Formation Tops</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Formation</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Top Depth (ft)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...formationTops]
                      .sort((a: FormationTop, b: FormationTop) => a.top_ft - b.top_ft)
                      .map((f: FormationTop, idx: number) => {
                        const fColors = getFormationColors(f.formation)
                        return (
                          <tr
                            key={`formation-table-${idx}`}
                            style={{ backgroundColor: fColors.bg }}
                          >
                            <td className="px-3 py-2 font-medium" style={{ color: fColors.text }}>{f.formation}</td>
                            <td className="px-3 py-2" style={{ color: fColors.text }}>{f.top_ft.toLocaleString()}</td>
                          </tr>
                        )
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="mt-6 p-4 bg-brand-50 rounded-lg">
            <h3 className="font-bold text-sm text-brand-900 mb-3">Legend</h3>
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 text-xs">
              {/* New Cement */}
              <div className="flex items-center gap-2">
                <svg width="24" height="16">
                  <defs>
                    <pattern id="legendNewCement" x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse">
                      <rect width="6" height="6" fill="#22c55e" />
                      <circle cx="3" cy="3" r="0.5" fill="#000" />
                    </pattern>
                  </defs>
                  <rect width="24" height="16" fill="url(#legendNewCement)" stroke="#22C55E" strokeWidth="1" />
                </svg>
                <span>New Cement</span>
              </div>
              {/* Existing cement */}
              <div className="flex items-center gap-2">
                <svg width="24" height="16">
                  <defs>
                    <pattern id="legendExisting" x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse">
                      <rect width="6" height="6" fill="#9CA3AF" />
                      <circle cx="3" cy="3" r="0.5" fill="#000" />
                    </pattern>
                  </defs>
                  <rect width="24" height="16" fill="url(#legendExisting)" stroke="#6B7280" strokeWidth="1" />
                </svg>
                <span>Existing Cement</span>
              </div>
              {/* CIBP / Bridge Plug — amber X cross */}
              <div className="flex items-center gap-2">
                <svg width="24" height="16">
                  <rect width="24" height="16" fill="#FEF3C7" stroke="#92400E" strokeWidth="1" strokeDasharray="3,3" />
                  <line x1="2" y1="2" x2="22" y2="14" stroke="#92400E" strokeWidth="1.5" />
                  <line x1="22" y1="2" x2="2" y2="14" stroke="#92400E" strokeWidth="1.5" />
                </svg>
                <span>CIBP / Bridge Plug</span>
              </div>
              {/* Packer — blue 3 horizontal bars */}
              <div className="flex items-center gap-2">
                <svg width="24" height="16">
                  <rect width="24" height="16" fill="#DBEAFE" stroke="#1D4ED8" strokeWidth="1" strokeDasharray="4,2" />
                  <line x1="2" y1="3" x2="22" y2="3" stroke="#1D4ED8" strokeWidth="2" />
                  <line x1="2" y1="8" x2="22" y2="8" stroke="#1D4ED8" strokeWidth="2" />
                  <line x1="2" y1="13" x2="22" y2="13" stroke="#1D4ED8" strokeWidth="2" />
                </svg>
                <span>Packer</span>
              </div>
              {/* Cement Retainer — purple inverted triangle */}
              <div className="flex items-center gap-2">
                <svg width="24" height="16">
                  <rect width="24" height="16" fill="#F3E8FF" stroke="#7C3AED" strokeWidth="1" strokeDasharray="4,2" />
                  <polygon points="12,2 22,13 2,13" fill="#7C3AED" opacity="0.7" />
                </svg>
                <span>Cement Retainer</span>
              </div>
              {/* DV Tool — teal diamond */}
              <div className="flex items-center gap-2">
                <svg width="24" height="16">
                  <rect width="24" height="16" fill="#CCFBF1" stroke="#0F766E" strokeWidth="1" strokeDasharray="3,3" />
                  <polygon points="12,2 22,8 12,14 2,8" fill="#0F766E" opacity="0.75" />
                </svg>
                <span>DV Tool</span>
              </div>
              {/* Straddle Packer — orange double bars */}
              <div className="flex items-center gap-2">
                <svg width="24" height="16">
                  <rect width="24" height="16" fill="#FED7AA" stroke="#C2410C" strokeWidth="1" strokeDasharray="5,2" />
                  <line x1="2" y1="2" x2="22" y2="2" stroke="#C2410C" strokeWidth="1.5" />
                  <line x1="2" y1="5" x2="22" y2="5" stroke="#C2410C" strokeWidth="1.5" />
                  <line x1="2" y1="8" x2="22" y2="8" stroke="#C2410C" strokeWidth="1.5" />
                  <line x1="2" y1="11" x2="22" y2="11" stroke="#C2410C" strokeWidth="1.5" />
                  <line x1="2" y1="14" x2="22" y2="14" stroke="#C2410C" strokeWidth="1.5" />
                </svg>
                <span>Straddle Packer</span>
              </div>
              {/* Production Perfs */}
              <div className="flex items-center gap-2">
                <svg width="30" height="6">
                  <line x1="0" y1="3" x2="30" y2="3" stroke="#DC2626" strokeWidth="3" />
                </svg>
                <span>Production Perfs</span>
              </div>
              {/* Casing */}
              <div className="flex items-center gap-2">
                <svg width="30" height="2">
                  <line x1="0" y1="1" x2="30" y2="1" stroke="#333" strokeWidth="3" />
                </svg>
                <span>Casing</span>
              </div>
              {/* Tubing */}
              <div className="flex items-center gap-2">
                <svg width="30" height="2">
                  <line x1="0" y1="1" x2="30" y2="1" stroke="#F59E0B" strokeWidth="2" strokeDasharray="3,3" />
                </svg>
                <span>Tubing</span>
              </div>
              {/* Tagged depth */}
              <div className="flex items-center gap-2">
                <svg width="24" height="16">
                  <polygon
                    points="12,2 18,8 12,14 6,8"
                    fill="#22c55e"
                    stroke="#16a34a"
                    strokeWidth="1"
                  />
                </svg>
                <span>Tagged Depth</span>
              </div>
              {/* Cut & Pulled casing */}
              <div className="flex items-center gap-2">
                <svg width="24" height="16">
                  <line x1="2" y1="2" x2="22" y2="14" stroke="#DC2626" strokeWidth="2" />
                  <line x1="22" y1="2" x2="2" y2="14" stroke="#DC2626" strokeWidth="2" />
                </svg>
                <span>Cut &amp; Pulled</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
)

export default AsPluggedWBD
